import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  ClaimPolicyUpdatedClaimsRow,
  claimPolicyPatchDashboardApi,
} from "../../../api/claimPolicyPatchDashboardApi";
import { trackToolAction } from "../../../utils/analytics";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";

const TABLE_ROW_LIMIT = 1000;
const EXPORT_ROW_LIMIT = 10000;

const formatDate = (value: string | null): string => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().replace("T", " ").slice(0, 19);
};

const escapeCsv = (value: string): string => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, "\"\"")}"`;
  }
  return value;
};

const buildCsv = (rows: ClaimPolicyUpdatedClaimsRow[]): string => {
  const rawKeys = Array.from(
    new Set(rows.flatMap((row) => Object.keys(row.rawRecord || {})))
  ).sort((a, b) => a.localeCompare(b));
  const header = [
    "videoId",
    "claimId",
    "claimedAt",
    "updatedAt",
    "sourceDocumentId",
    ...rawKeys,
  ];
  const lines = rows.map((row) =>
    [
      row.videoId ?? "",
      row.claimId ?? "",
      row.claimedAt ?? "",
      row.updatedAt ?? "",
      row.sourceDocumentId ?? "",
      ...rawKeys.map((key) => {
        const value = row.rawRecord?.[key];
        return value === null || typeof value === "undefined" ? "" : String(value);
      }),
    ]
      .map((cell) => escapeCsv(String(cell)))
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
};

const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const ClaimPolicyPatchUpdatedClaims = () => {
  const [rows, setRows] = useState<ClaimPolicyUpdatedClaimsRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await claimPolicyPatchDashboardApi.getUpdatedClaims(TABLE_ROW_LIMIT);
        if (!active) return;
        setRows(response.rows);
      } catch (loadError) {
        console.error("Failed to load updated claims", loadError);
        if (active) {
          setError("Unable to load updated claims right now.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const tableSubtitle = useMemo(
    () => `Showing ${rows.length} rows from Firestore (up to ${TABLE_ROW_LIMIT})`,
    [rows.length]
  );

  const handleExport = async () => {
    trackToolAction("DataHealthTool", "export");
    try {
      setExporting(true);
      setError(null);
      const response = await claimPolicyPatchDashboardApi.getUpdatedClaimsExport(EXPORT_ROW_LIMIT);
      const csv = buildCsv(response.rows);
      downloadCsv("claim-policy-patch-updated-claims.csv", csv);
    } catch (exportError) {
      console.error("Failed to export updated claims", exportError);
      setError("Unable to export CSV right now.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: "28px 32px 40px", display: "flex", flexDirection: "column", gap: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <div>
          <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
            All patched `videoId` and `claimId` records pulled from Firestore raw claims.
          </p>
          <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#6b7280" }}>{tableSubtitle}</p>
        </div>
        <Button
          label={exporting ? "Exporting..." : "Export CSV"}
          icon="pi pi-download"
          onClick={handleExport}
          disabled={exporting}
        />
      </div>

      {error && (
        <div className="p-tag-warning"
          style={{
            padding: "12px 16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            color: "#b91c1c",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <Card>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "220px" }}>
            <ProgressSpinner />
          </div>
        </Card>
      ) : (
        <Card>
          <DataTable
            value={rows}
            emptyMessage="No updated claim records found."
            stripedRows
            size="small"
            scrollable
            scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
          >
            <Column
              field="videoId"
              header="Video ID"
              body={(row) => (
                <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {row.videoId || "—"}
                </span>
              )}
            />
            <Column
              field="claimId"
              header="Claim ID"
              body={(row) => (
                <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {row.claimId || "—"}
                </span>
              )}
            />
            <Column field="claimedAt" header="Claimed At" body={(row) => formatDate(row.claimedAt)} />
            <Column field="updatedAt" header="Updated At" body={(row) => formatDate(row.updatedAt)} />
            <Column
              header="Raw Fields"
              body={(row) => Object.keys(row.rawRecord || {}).length}
              align="right"
              headerStyle={{ textAlign: "right" }}
            />
          </DataTable>
        </Card>
      )}
    </div>
  );
};

export default ClaimPolicyPatchUpdatedClaims;
